// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';
import LayerWms from '../../models/layers/layerwms';
import ColumnAliasHelper from '../../tools/utils/aliases';
import IGirafePanel from '../../tools/state/igirafepanel';
import Layer from '../../models/layers/layer';
import LayerFilterElement from './layerfilterelement';
import GroupLayer from '../../models/layers/grouplayer';
import { debounce } from '../../tools/utils/debounce';
import { UsedInTemplateOnly } from '../../decorators';
import FilterConditionElement from './filterconditionelement';
import LayerCsvExporter from './layercsvexporter';
import WfsFilter from '../../tools/wfs/wfsfilter';
import { LayerId } from '../../models/layerdefinitions';

class AdvancedFilterComponent extends GirafeHTMLElement implements IGirafePanel {
  templateUrl = './template.html';
  styleUrls = ['../../styles/common.css', './style.css'];

  isPanelVisible = false;
  panelTitle = 'advanced-filter-panel';
  panelTogglePath = 'interface.advancedFilterPanelVisible';
  loading = false;
  private readonly debouncedUpdateFilterItems = debounce(this.updateFilterItemsByLayerState.bind(this), 200);

  layers: LayerWms[] = [];
  layerFilterElements: LayerFilterElement[] = [];
  columnAliasHelper!: ColumnAliasHelper;
  layerIsRefreshing: Map<LayerId, boolean> = new Map();

  private csvDownloader!: LayerCsvExporter;

  constructor() {
    super('advanced-filter');
  }

  override render() {
    super.render();

    if (!this.isPanelVisible) {
      this.renderEmpty();
      return;
    }
    super.girafeTranslate();
  }

  registerEvents() {
    // Listen for filter changes on the layer list, triggered by changes in the simple filter component
    this.subscribe(/layers\.layersList\..*\.filter/, (oldFilter: WfsFilter, newFilter: WfsFilter, layer: LayerWms) =>
      this.onFilterChangedInLayerTree(oldFilter, newFilter, layer)
    );

    // Listen for layer visibility changes
    this.subscribe(/layers\.layersList\..*\.activeState/, (_oldValue: boolean, _newValue: boolean, layer: Layer) => {
      if (layer instanceof LayerWms && this.isPanelVisible && !this.state.loading) {
        // Debounce the filter element updates to avoid premature action / rendering while the layer tree is still changing
        void this.debouncedUpdateFilterItems();
      }
    });

    this.subscribe('loading', (_, loading: boolean) => {
      // Update the loading state of layers to control the spinner style in the template
      if (!loading && this.layerIsRefreshing.size > 0) {
        this.layerIsRefreshing = new Map();
        this.render();
      }
    });

    this.subscribe(`extendedState.${FilterConditionElement.drawingStateLocation}.features`, () => {
      // Render template a bit later to give the ConditionElement time to react to the state change as well
      //  and update the geometry
      setTimeout(() => this.render(), 500);
    });
  }

  private updateFilterableLayerList() {
    this.layers = this.context.layerManager
      .getFlattenedLayerTree(this.state.layers.layersList)
      .filter((l) => l instanceof LayerWms && l.active && l.wfsQueryable) as LayerWms[];
  }

  /**
   * Updates the list of filter elements based on the current state of the layers in the layer tree.
   */
  private async updateFilterItemsByLayerState() {
    // Set back the currently refreshing layer
    this.layerIsRefreshing = new Map();
    this.loading = true;
    this.render();

    //  Update which layers are active and queryable
    this.updateFilterableLayerList();

    // Remove filter elements that are no longer needed
    const filtersToBeRemoved = this.layerFilterElements.filter((filterElement) => {
      const layerInTree = this.layers.find((l) => l.id === filterElement.layer?.id);
      return (
        // Filter element does not have a layer yet
        filterElement.layer === undefined ||
        // Layer isn't in layer tree anymore
        (filterElement.layer && !layerInTree) ||
        // Filter in layer was removed by simple filter component
        (filterElement.layer && layerInTree!.filter === undefined && filterElement.hasAppliedConditions())
      );
    });
    filtersToBeRemoved.forEach((filterElement) => this.removeFilterElement(filterElement, false));

    // Add filter elements for layers that are new in the layer tree and do not have a filter element yet
    for (const layer of this.layers) {
      if (layer.hasFilter && !this.layerFilterElements.some((f) => f.layer?.id === layer.id)) {
        await this.addFilterElementFromLayer(layer);
      }
    }

    // Finally, if no filter element is present, add an empty one
    if (this.layerFilterElements.length === 0) {
      this.addEmptyFilterElement();
    }
    this.loading = false;
    this.render();
  }

  /**
   * Determines whether the given layer is selectable based on its visibility and absence in other filter elements.
   */
  @UsedInTemplateOnly()
  public isLayerSelectable(filterElement: LayerFilterElement, layer: LayerWms): boolean {
    return (
      this.layerFilterElements.filter((f: LayerFilterElement) => f.id != filterElement.id && f.layer?.id === layer.id)
        .length === 0
    );
  }

  /**
   * Combine layer name and name of the parent group for easier identification.
   */
  @UsedInTemplateOnly()
  public getLayerDisplayName(layer: LayerWms): string {
    let nameParts = [layer.name];
    if (layer.parent instanceof GroupLayer) {
      nameParts = [layer.parent.name, layer.name];
    }
    const uniqueNames = new Set(nameParts.map((part) => this.context.i18nManager.getTranslation(part)));
    return Array.from(uniqueNames).join(' - ');
  }

  public onFilterAdded() {
    this.addEmptyFilterElement();
    this.render();
  }

  private addEmptyFilterElement() {
    const filterElement = new LayerFilterElement(this.context, this.columnAliasHelper, this.layerFilterElements.length);
    this.layerFilterElements.push(filterElement);
  }

  /**
   * Uses the already existing filter object in the layer to create a filter element in the UI.
   */
  private async addFilterElementFromLayer(layer: LayerWms) {
    const filterElement = new LayerFilterElement(this.context, this.columnAliasHelper, this.layerFilterElements.length);
    await filterElement.setFromLayer(layer);
    this.layerFilterElements.push(filterElement);
  }

  public onFilterRemoved(filterElement: LayerFilterElement) {
    this.removeFilterElement(filterElement, true);
    this.render();
  }

  /**
   * Removes a filter element from the UI and optionally from the layer.
   * Note that the filter should only be removed from the layer if this was triggered by a user action in this component.
   */
  private removeFilterElement(filterElement: LayerFilterElement, removeFilterFromLayer: boolean = true) {
    const idx = this.layerFilterElements.indexOf(filterElement);
    if (idx !== -1) {
      filterElement.removeConditions();
      this.layerFilterElements.splice(idx, 1);
      if (removeFilterFromLayer && filterElement.layer?.filter) {
        // Note: Changing layer.filter will trigger the subscription that re-renders the component
        filterElement.layer.filter = undefined;
      }
    }
  }

  private onFilterChangedInLayerTree(oldFilter: WfsFilter, newFilter: WfsFilter, layer: LayerWms) {
    if (oldFilter === undefined || newFilter === undefined) {
      // Cleanly add or remove filter elements in the UI
      void this.updateFilterItemsByLayerState();
      return;
    }
    // Refresh the displayed filter element with the new filter
    for (const filterElement of this.layerFilterElements) {
      if (filterElement.layer?.id === layer.id) {
        filterElement.setFromLayer(layer).then(() => this.render());
        break;
      }
    }
  }

  public async onLayerChanged(filterElement: LayerFilterElement, e: Event) {
    if (!filterElement.conditions.length) {
      // Display an empty condition while layer metadata is being loaded
      filterElement.addCondition();
      this.render();
    }

    const layerId = (e.target as HTMLSelectElement).value;
    // Loose equality == is on purpose. layer.id can be of type number or string, but .value always returns a string
    const layer = this.layers.find((l) => l.id == layerId) as LayerWms;

    if (filterElement.hasAppliedConditions()) {
      // If the filter has already been applied to the layer, a new filter is created and the old one is deleted
      const newFilter = new LayerFilterElement(this.context, this.columnAliasHelper, this.layerFilterElements.length);
      this.layerFilterElements.push(newFilter);
      newFilter?.setLayer(layer).then((_) => this.render());
      // Remove filter from previous layer and from UI
      this.removeFilterElement(filterElement, true);
    } else {
      // Simply switch out layers
      filterElement.setLayer(layer).then((_) => this.render());
    }
  }

  public onConditionRemoved(filterElement: LayerFilterElement, condition: FilterConditionElement) {
    filterElement.removeCondition(condition);
    if (condition.applied) {
      // Update existing filter on layer to reflect the missing condition
      this.applyFilter(filterElement);
    }
    this.render();
  }

  public onConditionAdded(filterElement: LayerFilterElement) {
    filterElement.addCondition();
    this.render();
  }

  public onLogicalOperatorChanged(condition: FilterConditionElement, e: Event) {
    const selectedLogicalOperator = (e.target as HTMLSelectElement).value;
    condition.setLogicalOperator(selectedLogicalOperator);
    this.render();
  }

  public onAttributeChanged(filterElement: LayerFilterElement, condition: FilterConditionElement, e: Event) {
    const selectedAttributeName = (e.target as HTMLSelectElement).value;
    filterElement.setAttributeInCondition(condition, selectedAttributeName);
    this.render();
  }

  public onOperatorChanged(condition: FilterConditionElement, e: Event) {
    const selectedOperator = (e.target as HTMLSelectElement).value;
    condition.setOperator(selectedOperator);
    this.render();
  }

  public onValueChanged(condition: FilterConditionElement, e: Event) {
    condition.value = (e.target as HTMLInputElement).value;
    this.render();
  }

  public onValue2Changed(condition: FilterConditionElement, e: Event) {
    condition.value2 = (e.target as HTMLInputElement).value;
    this.render();
  }

  public onChoiceChanged(condition: FilterConditionElement, _e: Event) {
    const ul = this.getById(`choice-${condition.id}`);
    if (ul) {
      const checkboxes = ul.querySelectorAll('input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
      const checkedValues = Array.from(checkboxes).map((checkbox) => checkbox.value);
      if (checkedValues.length) {
        condition.value = JSON.stringify({ items: checkedValues });
      } else {
        condition.value = undefined;
      }
    }
    this.render();
  }

  public onConditionApplied(filterElement: LayerFilterElement, condition: FilterConditionElement) {
    if (condition.apply()) {
      this.applyFilter(filterElement);
    }
    this.render();
  }

  public onFilterInverted(filterElement: LayerFilterElement) {
    filterElement.toggleInverted();
    this.applyFilter(filterElement);
    this.render();
  }

  private applyFilter(filterElement: LayerFilterElement) {
    if (filterElement.layer) {
      this.layerIsRefreshing.set(filterElement.layer.id, true);
      // Note: Changing layer.filter will trigger the subscription that re-renders the component
      filterElement.layer.filter = filterElement.applyFilter();
    }
  }

  public async onDownloadLayerData(filterElements?: LayerFilterElement[]) {
    this.state.loading = true;
    filterElements ??= this.layerFilterElements;

    try {
      for (const filterElement of filterElements) {
        if (filterElement.hasNoneAppliedConditions()) {
          continue;
        }
        await this.csvDownloader.createCsvFile(filterElement.layer);
        if (this.csvDownloader.csvContentTruncated) {
          // Replace the standard wfs feature-limit warning with a more meaningful
          this.context.errorManager.pushMessage(
            'wfs-max-features-reached',
            'The CSV export has been truncated to the maximum number of features allowed for download',
            'warning'
          );
        }
      }
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  @UsedInTemplateOnly()
  public hasNonAppliedFilters(): boolean {
    return this.layerFilterElements.some((f) => f.hasNoneAppliedConditions() || !f.layer);
  }

  public togglePanel(visible: boolean) {
    this.isPanelVisible = visible;
    if (visible) {
      void this.updateFilterItemsByLayerState();
    } else {
      for (const filterElement of this.layerFilterElements) {
        filterElement.removeNonAppliedConditions();
      }
    }
    this.render();
  }

  protected override connectedCallback() {
    super.connectedCallback();
    this.columnAliasHelper = new ColumnAliasHelper(this.context.stateManager);
    this.csvDownloader = new LayerCsvExporter(this.context);

    this.updateFilterItemsByLayerState().then(() => {
      super.render();
      super.girafeTranslate();
      this.registerEvents();
    });
  }
}

export default AdvancedFilterComponent;
